import { useState } from 'react';
import { App, Button, Flex, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { IntInput, MoneyInput } from '@/shared/ui/inputs';
import { translateApiError } from '@/shared/api/errorToMessage';
import { formatDateTimeInTz } from '@/shared/lib/date';
import { useStoreContext } from '@/shared/lib/store-context';
import {
  useArchiveCategoryMutation,
  useCategoryPriceHistoryQuery,
  useCreateCategoryMutation,
  useListCategoriesQuery,
  useSetWholesalePriceMutation,
  useUnarchiveCategoryMutation,
  useUpdateCategoryMutation,
} from '@/entities/category';
import type { CategoryPriceHistoryEntry } from '@/entities/category';

export interface CategoryWindowProps {
  id?: string;
  parentId?: string;
  onClose: () => void;
}

/** §2.1: окно категории — корневая группа (без N) или основная категория (с N). */
export function CategoryWindow({ id, parentId, onClose }: CategoryWindowProps) {
  const { message } = App.useApp();
  const ctx = useStoreContext();
  const { data: categories = [] } = useListCategoriesQuery({ includeArchived: true });
  const existing = categories.find((c) => c.id === id);
  const roots = categories.filter((c) => c.parentId === null);
  const isMainExisting = !!id && !!existing?.parentId;
  const { data: priceHistory = [] } = useCategoryPriceHistoryQuery(id ?? '', { skip: !isMainExisting });

  const [name, setName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | undefined>(parentId);
  const [pcsPerBox, setPcsPerBox] = useState<number | null>(null);
  const [wholesalePrice, setWholesalePrice] = useState<string | null>(null);
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (existing && existing.id !== seededId) {
    setSeededId(existing.id);
    setName(existing.name);
    setSelectedParentId(existing.parentId ?? undefined);
    setPcsPerBox(existing.pcsPerBox);
    setWholesalePrice(existing.wholesalePrice);
  }

  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [setWholesale] = useSetWholesalePriceMutation();
  const [archive] = useArchiveCategoryMutation();
  const [unarchive] = useUnarchiveCategoryMutation();

  const isMain = selectedParentId !== undefined;
  const pcsLocked = Boolean(existing?.hasPostedDocuments);
  const busy = isCreating || isUpdating;

  async function handleSave() {
    try {
      if (!id) {
        await createCategory({ name, parentId: selectedParentId, pcsPerBox: isMain ? (pcsPerBox ?? undefined) : undefined }).unwrap();
      } else {
        await updateCategory({ id, body: { name, pcsPerBox: isMain && !pcsLocked ? (pcsPerBox ?? undefined) : undefined } }).unwrap();
        if (isMain && wholesalePrice !== existing?.wholesalePrice && wholesalePrice) {
          await setWholesale({ id, price: wholesalePrice }).unwrap();
        }
      }
      message.success('Сохранено');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  return (
    <FloatingWindow
      kind="category"
      title={id ? `Категория: ${existing?.name ?? ''}` : selectedParentId ? 'Новая категория' : 'Новая группа'}
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={busy} onClick={() => void handleSave()} disabled={!name}>
            Сохранить
          </Button>
          {id && existing ? (
            existing.isArchived ? (
              <Button onClick={() => void unarchive(id)}>Вернуть из архива</Button>
            ) : (
              <Button onClick={() => void archive(id)}>В архив</Button>
            )
          ) : null}
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Flex vertical gap={12} style={{ maxWidth: 420 }}>
        <Flex vertical gap={4}>
          <Typography.Text>Название</Typography.Text>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Flex>

        {!id ? (
          <Flex vertical gap={4}>
            <Typography.Text>Группа (пусто — новая корневая группа)</Typography.Text>
            <Select
              allowClear
              value={selectedParentId}
              onChange={(v) => setSelectedParentId(v)}
              options={roots.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Flex>
        ) : null}

        {isMain ? (
          <>
            <Flex vertical gap={4}>
              <Typography.Text>Штук в коробке (N)</Typography.Text>
              <IntInput value={pcsPerBox} onChange={setPcsPerBox} disabled={pcsLocked} />
              {pcsLocked ? <Typography.Text type="secondary">По категории есть проведённые документы</Typography.Text> : null}
            </Flex>
            {id ? (
              <Flex vertical gap={4}>
                <Typography.Text>Оптовая цена</Typography.Text>
                <MoneyInput value={wholesalePrice} onChange={setWholesalePrice} />
              </Flex>
            ) : null}
            {id && priceHistory.length > 0 ? (
              <Flex vertical gap={4}>
                <Typography.Text>История цены</Typography.Text>
                <Table<CategoryPriceHistoryEntry>
                  size="small"
                  rowKey="id"
                  dataSource={priceHistory}
                  columns={priceHistoryColumns(ctx.timezone)}
                  pagination={false}
                />
              </Flex>
            ) : null}
          </>
        ) : null}
      </Flex>
    </FloatingWindow>
  );
}

/** §2.1 [ПРЕДЛ]: «История цены» — сырые записи `AuditLog` за `WHOLESALE_PRICE_CHANGED`. */
function priceHistoryColumns(timezone: string): ColumnsType<CategoryPriceHistoryEntry> {
  return [
    { title: 'Дата', width: 130, render: (_v, r) => formatDateTimeInTz(r.createdAt, timezone) },
    { title: 'Было', width: 90, align: 'right', render: (_v, r) => r.before.wholesalePrice ?? '—' },
    { title: 'Стало', width: 90, align: 'right', render: (_v, r) => r.after.wholesalePrice ?? '—' },
    {
      title: 'Источник',
      render: (_v, r) => (r.after.source === 'PURCHASE' ? `Приход ${r.after.documentNumber ?? ''}` : 'Вручную'),
    },
  ];
}
